import {
  Box,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_TRANSACTION_STATISTICS, EmployerStatisticsData, EmployerStatisticsVars } from "../../graphql/operations";
import { formatCurrency } from "../../utils/formatters";
import EmployeeSpendChart from "../charts/EmployeeSpendChart";
import { useData } from "../../utils/useData";

const ToggleStyle = {
  py: 0.5,
  fontSize: 12,
  border: 0,
  "&.Mui-selected": {
    backgroundColor: "transparent",
    fontWeight: 700
  },
  "&.Mui-disabled": {
    border: "0px solid transparent"
  }
};

export const EmployeeSpend = () => {
  const [slot, setSlot] = useState("12m");
  const { employerId } = useData();

  const { data, loading, error } = useQuery<EmployerStatisticsData, EmployerStatisticsVars>(
    GET_TRANSACTION_STATISTICS,
    {
      variables: { employerId },
    }
  );

  const totalSpend: { [key: string]: number } = useMemo(() => {
    if (loading || !data) return { "1m": 0, "12m": 0 };

    const currentMonthTotal = data.employerStatistics.employeeSpend.currentMonthDailyStats.reduce(
      (sum, stat) => sum + stat.totalAmount,
      0
    );

    const yearTotal = data.employerStatistics.employeeSpend.monthlyStats.reduce(
      (sum, stat) => sum + stat.totalAmount,
      0
    );

    return {
      "1m": currentMonthTotal,
      "12m": yearTotal
    };
  }, [data, loading]);

  // Sort monthly stats chronologically from 12 months ago to current month
  const sortedMonthlyStats = useMemo(() => {
    if (!data?.employerStatistics.employeeSpend.monthlyStats) return [];

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // 1-based month
    const currentYear = currentDate.getFullYear();

    // Calculate the date 12 months ago
    const twelveMonthsAgo = new Date(currentDate);
    twelveMonthsAgo.setMonth(currentDate.getMonth() - 11); // -11 because we want to include current month
    const startMonth = twelveMonthsAgo.getMonth() + 1; // 1-based month
    const startYear = twelveMonthsAgo.getFullYear();

    // Filter and sort the stats
    return [...data.employerStatistics.employeeSpend.monthlyStats]
      .filter(stat => {
        // Convert stat date to a comparable format
        const statDate = new Date(stat.year, stat.month - 1);
        const startDate = new Date(startYear, startMonth - 1);
        const endDate = new Date(currentYear, currentMonth - 1);
        
        // Keep only stats within our 12-month window
        return statDate >= startDate && statDate <= endDate;
      })
      .sort((a, b) => {
        // Sort by year first, then by month
        if (a.year !== b.year) {
          return a.year - b.year;
        }
        return a.month - b.month;
      });
  }, [data]);

  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    if (newAlignment) setSlot(newAlignment);
  };

  if (error) {
    return (
      <Typography color="error">
        Error loading transaction statistics: {error.message}
      </Typography>
    );
  }

  return (
    <>
      <Grid item>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, fontSize: 15, color: "#454545" }}
            >
              User Spend
            </Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1 }}>
              {formatCurrency(totalSpend[slot] ?? 0)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={{ xs: "center", sm: "flex-end" }}
              sx={{ mt: 3, mr: 2 }}
            >
              <ToggleButtonGroup
                exclusive
                onChange={handleChange}
                size="small"
                value={slot}
              >
                <ToggleButton
                  disabled={slot === "12m"}
                  value="12m"
                  className={"RangeToggle"}
                  sx={ToggleStyle}
                >
                  12M
                </ToggleButton>
                <ToggleButton
                  disabled={slot === "1m"}
                  value="1m"
                  className={"RangeToggle"}
                  sx={ToggleStyle}
                >
                  1M
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Box sx={{ pt: 1 }}>
        <EmployeeSpendChart
          slot={slot}
          monthlyStats={sortedMonthlyStats}
          dailyStats={data?.employerStatistics.employeeSpend.currentMonthDailyStats}
          loading={loading}
        />
      </Box>
    </>
  );
};
